/**
 * component-product-gallery.js -- Product Gallery Web Component for Mirai Online (Phase 3)
 *
 * <product-gallery> custom element:
 *  - Opens a full-screen lightbox when any gallery image is clicked/tapped
 *  - Lightbox: focus trap + inert background, Escape/close-button/backdrop close,
 *    Left/Right arrow + prev/next button navigation (wrap-around), image opacity swap
 *  - Pan (pointer drag when zoomed) + pinch-zoom (two-finger touch), max 3x, reset on close
 *  - Mobile carousel dot sync via IntersectionObserver (threshold 0.5)
 *  - Theme editor compatible via shopify:section:load reinit
 *  - Light DOM only (no Shadow DOM) so design tokens reach in
 *
 * Security notes (per threat model T-03-02/03):
 *  - Lightbox image src comes from server-generated data-highres (Shopify CDN url),
 *    assigned via img.src -- never via innerHTML. No user string reaches the DOM as markup.
 *  - alt text is copied from the source img's alt attribute (already escaped server-side).
 */

/* ============================================================
   PRODUCT GALLERY WEB COMPONENT
   ============================================================ */

class ProductGallery extends HTMLElement {
  constructor() {
    super();

    // Stable bound references for add/removeEventListener symmetry
    this._boundKeydown = this._handleKeydown.bind(this);
    this._boundBackdropClick = this._handleBackdropClick.bind(this);
    this._boundTouchStart = this._handleTouchStart.bind(this);
    this._boundTouchMove = this._handleTouchMove.bind(this);
    this._boundTouchEnd = this._handleTouchEnd.bind(this);
    this._boundPointerDown = this._handlePointerDown.bind(this);
    this._boundPointerMove = this._handlePointerMove.bind(this);
    this._boundPointerUp = this._handlePointerUp.bind(this);

    // Amazon-style thumbnail rail + hover-zoom (desktop)
    this._boundThumbActivate = this._handleThumbActivate.bind(this);
    this._boundStageMove = this._handleStageMove.bind(this);
    this._boundStageLeave = this._handleStageLeave.bind(this);

    // State
    this._lightboxOpen = false;
    this._activeIndex = 0;
    this._lastFocused = null;
    this._releaseFocusTrap = null;
    this._observer = null;

    // Pan/zoom state
    this._scale = 1;
    this._translateX = 0;
    this._translateY = 0;
    this._panning = false;
    this._panStartX = 0;
    this._panStartY = 0;
    this._pinchStartDist = 0;
    this._pinchStartScale = 1;
  }

  connectedCallback() {
    this._init();
    this.addEventListener('shopify:section:load', () => this._reinit());
  }

  disconnectedCallback() {
    this._teardown();
  }

  _init() {
    this._images = Array.from(this.querySelectorAll('[data-gallery-image]'));
    this._dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this._galleryItems = Array.from(this.querySelectorAll('.pdp-gallery__item'));
    this._thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this._thumbRail = this.querySelector('[data-thumb-rail]');
    this._stage = this.querySelector('[data-gallery-stage]');
    this._lightbox = this.querySelector('[data-lightbox]');
    this._lightboxImg = this._lightbox ? this._lightbox.querySelector('[data-lightbox-img]') : null;

    // Open lightbox on image click
    this._images.forEach((img, index) => {
      img.style.cursor = 'pointer';
      img.addEventListener('click', () => this.openLightbox(index));
    });

    this._setupThumbs();
    this._setupHoverZoom();

    if (this._lightbox) {
      const closeBtn = this._lightbox.querySelector('[data-lightbox-close]');
      const prevBtn = this._lightbox.querySelector('[data-lightbox-prev]');
      const nextBtn = this._lightbox.querySelector('[data-lightbox-next]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closeLightbox());
      if (prevBtn) prevBtn.addEventListener('click', () => this.navigate(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.navigate(1));

      // Backdrop click (only when the overlay itself is the target)
      this._lightbox.addEventListener('click', this._boundBackdropClick);

      // Touch (pinch-zoom + pan) on the lightbox image
      if (this._lightboxImg) {
        this._lightboxImg.addEventListener('touchstart', this._boundTouchStart, { passive: false });
        this._lightboxImg.addEventListener('touchmove', this._boundTouchMove, { passive: false });
        this._lightboxImg.addEventListener('touchend', this._boundTouchEnd);
        this._lightboxImg.addEventListener('pointerdown', this._boundPointerDown);
      }
    }

    // Global keydown (Escape + arrows) only acts while open
    document.addEventListener('keydown', this._boundKeydown);

    this._setupDotSync();
  }

  _teardown() {
    // WR-04: if torn down while the lightbox is open, fully close first so the
    // background inert is released, _lightboxOpen is reset, and focus is
    // restored — otherwise .pdp-gallery/.pdp-details stay permanently inert.
    if (this._lightboxOpen) this.closeLightbox();

    document.removeEventListener('keydown', this._boundKeydown);

    if (this._thumbRail) {
      this._thumbRail.removeEventListener('click', this._boundThumbActivate);
      this._thumbRail.removeEventListener('mouseover', this._boundThumbActivate);
    }
    if (this._stage) {
      this._stage.removeEventListener('mousemove', this._boundStageMove);
      this._stage.removeEventListener('mouseleave', this._boundStageLeave);
      this._stage.classList.remove('is-zooming');
    }

    // WR-03: defensively remove the document-level pan listeners and reset pan
    // state in case teardown happens mid-drag (closeLightbox/_resetZoom do not
    // detach these document listeners).
    document.removeEventListener('pointermove', this._boundPointerMove);
    document.removeEventListener('pointerup', this._boundPointerUp);
    this._panning = false;

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
  }

  _reinit() {
    this._teardown();
    this._init();
  }

  /* ---------- Mobile dot sync ---------- */

  _setupDotSync() {
    if (!this._dots.length || !this._galleryItems.length || !('IntersectionObserver' in window)) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = this._galleryItems.indexOf(entry.target);
            if (index > -1) this._setActiveDot(index);
          }
        });
      },
      { root: null, threshold: 0.5 }
    );

    this._galleryItems.forEach((item) => this._observer.observe(item));
  }

  _setActiveDot(index) {
    this._activeIndex = index;
    this._dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('pdp-gallery__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
    this._syncThumbs(index);
  }

  /* ---------- Thumbnail rail (Amazon-style, desktop) ---------- */

  _setupThumbs() {
    if (!this._thumbRail) return;
    // Delegate click + hover so a single pair of listeners covers every thumb.
    this._thumbRail.addEventListener('click', this._boundThumbActivate);
    this._thumbRail.addEventListener('mouseover', this._boundThumbActivate);
  }

  _handleThumbActivate(e) {
    const thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;
    const index = parseInt(thumb.dataset.index, 10);
    if (isNaN(index)) return;
    this.setActive(index);
  }

  /**
   * Public: make image `index` the active one. Switches the desktop stage,
   * highlights its thumbnail + dot, and scrolls the mobile carousel to it.
   * Called by the thumbnail rail and by <variant-selector> on variant change.
   */
  setActive(index) {
    if (index < 0 || index >= this._galleryItems.length) return;
    this._activeIndex = index;

    this._galleryItems.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });
    this._syncThumbs(index);
    this._setActiveDotOnly(index);

    // Mobile: bring the matching slide into the horizontal carousel viewport.
    // block:'nearest' keeps the page from jumping vertically.
    const target = this._galleryItems[index];
    if (target && this._stage && this._stage.scrollWidth > this._stage.clientWidth) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  _syncThumbs(index) {
    if (!this._thumbs || !this._thumbs.length) return;
    this._thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('pdp-thumb--active', isActive);
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  // Dot-only update (no thumb recursion) used by setActive.
  _setActiveDotOnly(index) {
    this._dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('pdp-gallery__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /* ---------- Hover zoom (desktop, fine pointer) ---------- */

  _setupHoverZoom() {
    if (!this._stage) return;
    this._zoomQuery =
      window.matchMedia &&
      window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    if (!this._zoomQuery || !this._zoomQuery.matches) return;
    this._stage.addEventListener('mousemove', this._boundStageMove);
    this._stage.addEventListener('mouseleave', this._boundStageLeave);
  }

  _handleStageMove(e) {
    if (this._zoomQuery && !this._zoomQuery.matches) return;
    const activeImg = this._stage.querySelector('.pdp-gallery__item.is-active .pdp-gallery__image');
    if (!activeImg) return;
    const rect = this._stage.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    activeImg.style.transformOrigin =
      Math.max(0, Math.min(100, x)) + '% ' + Math.max(0, Math.min(100, y)) + '%';
    this._stage.classList.add('is-zooming');
  }

  _handleStageLeave() {
    if (!this._stage) return;
    this._stage.classList.remove('is-zooming');
    const activeImg = this._stage.querySelector('.pdp-gallery__item.is-active .pdp-gallery__image');
    if (activeImg) activeImg.style.transformOrigin = '';
  }

  /* ---------- Lightbox open / close / navigate ---------- */

  openLightbox(index) {
    if (!this._lightbox || !this._lightboxImg) return;

    this._activeIndex = index;
    this._lastFocused = this._images[index] || document.activeElement;
    this._loadImage(index);

    this._lightbox.removeAttribute('hidden');
    // Force reflow so the opacity transition runs from the open class
    void this._lightbox.offsetWidth;
    this._lightbox.classList.add('pdp-lightbox--open');
    this._lightboxOpen = true;

    this._setBackgroundInert(true);

    const closeBtn = this._lightbox.querySelector('[data-lightbox-close]');
    if (closeBtn) closeBtn.focus();

    this._releaseFocusTrap = this._trapFocus(this._lightbox);
  }

  closeLightbox() {
    if (!this._lightbox || !this._lightboxOpen) return;

    this._lightbox.classList.remove('pdp-lightbox--open');
    this._lightbox.setAttribute('hidden', '');
    this._lightboxOpen = false;

    this._resetZoom();
    this._setBackgroundInert(false);

    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
    if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
      this._lastFocused.focus();
    }
  }

  navigate(delta) {
    if (!this._images.length) return;
    const total = this._images.length;
    this._activeIndex = (this._activeIndex + delta + total) % total;
    this._resetZoom();
    this._loadImage(this._activeIndex);
  }

  _loadImage(index) {
    const sourceImg = this._images[index];
    if (!sourceImg || !this._lightboxImg) return;
    const highRes = sourceImg.dataset.highres || sourceImg.currentSrc || sourceImg.src;
    this._lightboxImg.src = highRes;
    this._lightboxImg.alt = sourceImg.alt || '';
    this._lightbox.setAttribute('aria-label', `Product image ${index + 1} of ${this._images.length}`);
  }

  /* ---------- Background inert (focus containment) ---------- */

  _setBackgroundInert(on) {
    const section = this.closest('.main-product') || this.parentElement;
    if (!section) return;
    const targets = Array.from(section.querySelectorAll('.pdp-thumbs, .pdp-gallery, .pdp-gallery__dots, .pdp-details'));
    targets.forEach((el) => {
      if (on) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    });
  }

  /* ---------- Focus trap (re-queries on every Tab, per cart-drawer pattern) ---------- */

  _trapFocus(container) {
    const focusableSelectors =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(container.querySelectorAll(focusableSelectors));

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeydown);
    return () => container.removeEventListener('keydown', handleKeydown);
  }

  /* ---------- Event handlers ---------- */

  _handleKeydown(e) {
    if (!this._lightboxOpen) return;
    if (e.key === 'Escape') {
      this.closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      this.navigate(-1);
    } else if (e.key === 'ArrowRight') {
      this.navigate(1);
    }
  }

  _handleBackdropClick(e) {
    if (e.target === this._lightbox) this.closeLightbox();
  }

  /* ---------- Pinch-zoom (touch) + pan ---------- */

  _handleTouchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      this._pinchStartDist = this._touchDistance(e.touches);
      this._pinchStartScale = this._scale;
    } else if (e.touches.length === 1 && this._scale > 1) {
      this._panning = true;
      this._panStartX = e.touches[0].clientX - this._translateX;
      this._panStartY = e.touches[0].clientY - this._translateY;
    }
  }

  _handleTouchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = this._touchDistance(e.touches);
      if (this._pinchStartDist > 0) {
        const next = this._pinchStartScale * (dist / this._pinchStartDist);
        this._scale = Math.min(Math.max(next, 1), 3);
        if (this._scale === 1) {
          this._translateX = 0;
          this._translateY = 0;
        }
        this._applyTransform();
      }
    } else if (e.touches.length === 1 && this._panning) {
      e.preventDefault();
      this._translateX = e.touches[0].clientX - this._panStartX;
      this._translateY = e.touches[0].clientY - this._panStartY;
      this._applyTransform();
    }
  }

  _handleTouchEnd() {
    this._panning = false;
    this._pinchStartDist = 0;
  }

  _touchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  }

  /* ---------- Pan via pointer (desktop drag when zoomed) ---------- */

  _handlePointerDown(e) {
    if (this._scale <= 1) return;
    this._panning = true;
    this._panStartX = e.clientX - this._translateX;
    this._panStartY = e.clientY - this._translateY;
    document.addEventListener('pointermove', this._boundPointerMove);
    document.addEventListener('pointerup', this._boundPointerUp);
  }

  _handlePointerMove(e) {
    if (!this._panning) return;
    this._translateX = e.clientX - this._panStartX;
    this._translateY = e.clientY - this._panStartY;
    this._applyTransform();
  }

  _handlePointerUp() {
    this._panning = false;
    document.removeEventListener('pointermove', this._boundPointerMove);
    document.removeEventListener('pointerup', this._boundPointerUp);
  }

  _applyTransform() {
    if (!this._lightboxImg) return;
    this._lightboxImg.style.transform =
      `scale(${this._scale}) translate(${this._translateX / this._scale}px, ${this._translateY / this._scale}px)`;
  }

  _resetZoom() {
    this._scale = 1;
    this._translateX = 0;
    this._translateY = 0;
    this._panning = false;
    if (this._lightboxImg) this._lightboxImg.style.transform = '';
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
